import { Controller } from "@hotwired/stimulus"

// Define constants
const MIN_AGE = 18
const MAX_AGE = 99
const MIN_HEIGHT_CM = 122
const MAX_HEIGHT_CM = 213

export default class extends Controller {
  static targets = [
    "minVal",
    "maxVal",
    "minTooltip",
    "maxTooltip",
    "minInput",
    "maxInput",
    "minAgeField",
    "maxAgeField",
    "minHeightField",
    "maxHeightField",
  ]

  minGap = 0
  heightMappings = []
  sliderMinValue
  sliderMaxValue

  connect() {
    this.initializeValues()
    requestAnimationFrame(() => {
      this.forceLayoutAndSetArea()
    })

    // Listen for the reset event
    window.addEventListener("resetSlider", (event) => {
      this.resetSlider(event.detail)
    })
  }

  initializeValues() {
    if (this.hasMinAgeFieldTarget) {
      this.sliderMinValue = MIN_AGE
      this.sliderMaxValue = MAX_AGE
      this.minValTarget.value = this.minAgeFieldTarget.value || MIN_AGE
      this.maxValTarget.value = this.maxAgeFieldTarget.value || MAX_AGE
      this.minInputTarget.value = this.minAgeFieldTarget?.value || MIN_AGE
      this.maxInputTarget.value = this.maxAgeFieldTarget?.value || MAX_AGE
    } else if (this.hasMinHeightFieldTarget) {
      this.heightMappings = JSON.parse(this.data.get("heightMappings"))
      this.sliderMinValue = MIN_HEIGHT_CM
      this.sliderMaxValue = MAX_HEIGHT_CM
      this.minValTarget.value =
        this.extractCmValue(this.minHeightFieldTarget?.value) || MIN_HEIGHT_CM
      this.maxValTarget.value =
        this.extractCmValue(this.maxHeightFieldTarget?.value) || MAX_HEIGHT_CM
      this.minInputTarget.value =
        this.extractCmValue(this.minHeightFieldTarget?.value) || MIN_HEIGHT_CM
      this.maxInputTarget.value =
        this.extractCmValue(this.maxHeightFieldTarget?.value) || MAX_HEIGHT_CM
    }
    this.updateTooltips()
  }

  extractCmValue(value) {
    if (value) {
      const match = this.heightMappings.find(
        (mapping) => mapping.string === value.trim()
      )

      if (match) {
        return match.cm
      }
    }
    return null
  }

  getClosestValue(value) {
    const closest = this.heightMappings.reduce((prev, curr) => {
      return Math.abs(curr.cm - value) < Math.abs(prev.cm - value) ? curr : prev
    })
    return closest.cm
  }

  getStringForValue(value) {
    const mapping = this.heightMappings.find((item) => item.cm == value)
    return mapping ? mapping.string : ""
  }

  getAbbreviationForValue(value) {
    const mapping = this.heightMappings.find((item) => item.cm == value)
    return mapping ? mapping.abbreviation : ""
  }

  updateTooltips() {
    if (this.hasMinHeightFieldTarget) {
      this.minTooltipTarget.innerHTML = `<span class="min-tooltip-number">${this.getAbbreviationForValue(
        this.minValTarget.value
      )}</span>`
      this.maxTooltipTarget.innerHTML = `<span class="max-tooltip-number">${this.getAbbreviationForValue(
        this.maxValTarget.value
      )}</span>`
    } else if (this.hasMinAgeFieldTarget) {
      this.minTooltipTarget.innerHTML = `<span class="min-tooltip-number">${this.minValTarget.value}</span>`
      this.maxTooltipTarget.innerHTML = `<span class="max-tooltip-number">${this.maxValTarget.value}</span>`
    }
  }

  resetSlider({ min, max, type }) {
    if (
      type === "age" &&
      this.hasMinAgeFieldTarget &&
      this.hasMaxAgeFieldTarget
    ) {
      this.minValTarget.value = min
      this.maxValTarget.value = max
    } else if (
      type === "height" &&
      this.hasMinHeightFieldTarget &&
      this.hasMaxHeightFieldTarget
    ) {
      this.minValTarget.value = min
      this.maxValTarget.value = max
    }
    this.updateTooltips()
    this.updateFields()
    this.setArea()
  }

  slideMin() {
    let gap =
      parseInt(this.maxValTarget.value) - parseInt(this.minValTarget.value)
    if (gap <= this.minGap) {
      this.minValTarget.value = parseInt(this.maxValTarget.value) - this.minGap
    }
    if (this.hasMinHeightFieldTarget) {
      this.minValTarget.value = this.getClosestValue(this.minValTarget.value)
    }
    this.updateTooltips()
    this.minInputTarget.value = this.minValTarget.value
    this.updateFields()
    this.setArea()
  }

  slideMax() {
    let gap =
      parseInt(this.maxValTarget.value) - parseInt(this.minValTarget.value)
    if (gap <= this.minGap) {
      this.maxValTarget.value = parseInt(this.minValTarget.value) + this.minGap
    }
    if (this.hasMaxHeightFieldTarget) {
      this.maxValTarget.value = this.getClosestValue(this.maxValTarget.value)
    }
    this.updateTooltips()
    this.maxInputTarget.value = this.maxValTarget.value
    this.updateFields()
    this.setArea()
  }

  setArea() {
    const thumbLeftPosition =
      (this.minValTarget.value - this.sliderMinValue) /
      (this.sliderMaxValue - this.sliderMinValue)
    const thumbRightPosition =
      (this.maxValTarget.value - this.sliderMinValue) /
      (this.sliderMaxValue - this.sliderMinValue)

    const spaceLeft =
      this.minValTarget.offsetWidth - this.minTooltipTarget.offsetWidth
    const spaceRight =
      this.maxValTarget.offsetWidth - this.maxTooltipTarget.offsetWidth

    this.minTooltipTarget.style.left = `${
      thumbLeftPosition * spaceLeft - 2.5
    }px`
    this.maxTooltipTarget.style.left = `${
      thumbRightPosition * spaceRight + 2.5
    }px`
  }

  setMinInput() {
    let minAge = parseInt(this.minInputTarget.value)
    if (minAge < this.sliderMinValue) {
      this.minInputTarget.value = this.sliderMinValue
    }
    this.minValTarget.value = this.minInputTarget.value
    this.slideMin()
  }

  setMaxInput() {
    let maxAge = parseFloat(this.maxInputTarget.value)
    if (maxAge > this.sliderMaxValue) {
      this.maxInputTarget.value = this.sliderMaxValue
    }
    this.maxValTarget.value = this.maxInputTarget.value
    this.slideMax()
  }

  updateFields() {
    if (this.hasMinAgeFieldTarget) {
      const min = parseInt(this.minValTarget.value)
      const max = parseInt(this.maxValTarget.value)

      const minSelected = min > MIN_AGE
      const maxSelected = max < MAX_AGE

      if (minSelected || maxSelected) {
        this.minAgeFieldTarget.value = minSelected ? min : MIN_AGE
        this.maxAgeFieldTarget.value = maxSelected ? max : MAX_AGE
      } else {
        this.minAgeFieldTarget.value = ""
        this.maxAgeFieldTarget.value = ""
      }
    } else if (this.hasMinHeightFieldTarget) {
      const min = parseInt(this.minValTarget.value)
      const max = parseInt(this.maxValTarget.value)

      const minSelected = min > MIN_HEIGHT_CM
      const maxSelected = max < MAX_HEIGHT_CM

      if (minSelected || maxSelected) {
        this.minHeightFieldTarget.value = this.getStringForValue(
          minSelected ? min : MIN_HEIGHT_CM
        )
        this.maxHeightFieldTarget.value = this.getStringForValue(
          maxSelected ? max : MAX_HEIGHT_CM
        )
      } else {
        this.minHeightFieldTarget.value = ""
        this.maxHeightFieldTarget.value = ""
      }
    }
  }

  forceLayoutAndSetArea() {
    // Temporarily show hidden collapse element to measure tooltips
    const collapseEl = this.element.closest(".collapse")

    if (
      collapseEl &&
      collapseEl.classList.contains("collapse") &&
      !collapseEl.classList.contains("show")
    ) {
      collapseEl.style.visibility = "hidden"
      collapseEl.style.display = "block"

      // Wait one frame for layout to apply
      requestAnimationFrame(() => {
        this.setArea()
        this.updateTooltips()

        // Re-hide the element
        collapseEl.style.removeProperty("display")
        collapseEl.style.removeProperty("visibility")
      })
    } else {
      // Already visible – just set
      this.setArea()
      this.updateTooltips()
    }
  }
}
